import { useLanguage } from '../contexts/LanguageContext';
import { Code2, Palette, Gauge, Cloud, Check } from 'lucide-react';

export const PricingSection = () => {
  const { t, language } = useLanguage();

  const plans = [
    {
      icon: Code2,
      titleKey: 'pricing1Title',
      price: '3 500',
      period: language === 'no' ? '' : '',
      descKey: 'pricing1Desc',
      features: language === 'no' 
        ? ['Skreddersydd design', 'Responsivt for alle enheter', 'Moderne teknologi', 'SEO-grunnlag inkludert']
        : ['Custom design', 'Responsive for all devices', 'Modern technology', 'SEO foundation included'],
      popular: true,
    },
    {
      icon: Palette,
      titleKey: 'pricing2Title',
      price: '2 490',
      period: '',
      descKey: 'pricing2Desc',
      features: language === 'no'
        ? ['Design-oppgradering', 'Forbedret brukeropplevelse', 'Ny funksjonalitet', 'Mobiltilpasning']
        : ['Design upgrade', 'Improved user experience', 'New functionality', 'Mobile optimization'],
      popular: false,
    },
    {
      icon: Gauge,
      titleKey: 'pricing3Title',
      price: '1 900',
      period: '',
      descKey: 'pricing3Desc',
      features: language === 'no'
        ? ['Hastighetsoptimalisering', 'SEO-analyse og forbedring', 'Core Web Vitals', 'Søkemotorrangering']
        : ['Speed optimization', 'SEO analysis & improvement', 'Core Web Vitals', 'Search engine ranking'],
      popular: false,
    },
    {
      icon: Cloud,
      titleKey: 'pricing4Title',
      price: '490',
      period: language === 'no' ? '/mnd' : '/mo',
      descKey: 'pricing4Desc',
      features: language === 'no'
        ? ['Sikker hosting', 'Løpende oppdateringer', 'Teknisk support', 'Backup av data']
        : ['Secure hosting', 'Ongoing updates', 'Technical support', 'Data backup'],
      popular: false,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section title */}
        <h2
          data-testid="pricing-title"
          className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground mb-6 fade-in-up"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          {t('pricingTitle')}
        </h2>
        <p className="text-lg text-muted-foreground mb-16 max-w-2xl fade-in-up stagger-1">
          {t('pricingSubtitle')}
        </p>

        {/* Pricing grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {plans.map((plan, index) => (
            <div
              key={plan.titleKey}
              data-testid={`pricing-card-${index + 1}`}
              className={`relative glass-card p-8 rounded-2xl fade-in-up stagger-${index + 1} ${
                plan.popular ? 'border-primary/50' : ''
              }`}
            >
              {/* Popular badge */}
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                    {language === 'no' ? 'Mest populær' : 'Most popular'}
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                <plan.icon className="w-7 h-7 text-primary" />
              </div>

              {/* Title */}
              <h3
                className="text-xl font-semibold text-foreground mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                {t(plan.titleKey)}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-6">
                {t(plan.descKey)}
              </p>

              {/* Price */}
              <div className="mb-6">
                <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                <span className="text-lg text-muted-foreground ml-1">NOK{plan.period}</span>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <button
                data-testid={`pricing-cta-${index + 1}`}
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className={`w-full py-3 rounded-full font-medium transition-all ${
                  plan.popular
                    ? 'bg-primary hover:bg-primary/90 text-primary-foreground btn-glow'
                    : 'bg-transparent border border-border hover:border-primary/50 text-foreground'
                }`}
              >
                {language === 'no' ? 'Kom i gang' : 'Get started'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
